import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Grid,
  Typography,
  Checkbox,
  TextField,
  InputLabel,
  FormControlLabel,
} from '@mui/material';

// COMPONENTS
import CarrierPrefixList from './CarrierPrefixList';
import CarrierPrefixSingle from './PrefixSingle';
import ServiceCarrierDeviceList from './Device/ServiceCarrierDeviceList';
import CarrierDeviceSingle from './Device/CarrierDeviceSingleView';
import CarrierPortedNumberList from './PortedNumberList';
import CarrierPortedNumberSingle from './PortedNumberSingle';
import BackButton from '../../../../common/BackButton';

// UTILS
import { deepCompare } from '../../../../../utils/compare';

// NETWORK
import useCarrierRequest from '../../../../../requests/CarrierRequest';

// BREADCRUMB
import { useBreadcrumb } from '../BreadcrumbContext';
import ConfirmModal from '../../../../common/modals/ConfirmModal';

const CarrierSingleView = ({
  carrier,
  country,
  editing,
  errorHandler,
  navigate,
}) => {
  const params = useParams();
  const { grandChild, grandChildId } = params;

  const { addBreadcrumb, dropBreadcrumb } = useBreadcrumb();

  const [view, setView] = useState({});
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [portedNumbers, setPortedNumbers] = useState([]);
  const [portedNumbersLoading, setPortedNumbersLoading] = useState(true);
  const [prefixes, setPrefixes] = useState([]);
  const [prefixesLoading, setPrefixesLoading] = useState(true);

  const [selectedCarrierDevice, setSelectedCarrierDevice] = useState({});
  const [selectedCarrierPrefix, setSelectedCarrierPrefix] = useState({});
  const [selectedCarrierPortedNumber, setSelectedCarrierPortedNumber] =
    useState({});

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(editing || false);
  const [edits, setEdits] = useState({});

  const {
    createNewCarrier,
    updateCarrier,
    deleteCarrierById, // Doesn't exits in requests, needs to be added
    getCarrierDevices,
    getCarrierPortedNumbers,
    getCarrierPrefixes,
  } = useCarrierRequest();

  useEffect(() => {
    addBreadcrumb(1, `Carrier: ${carrier?.carrier_name || 'NEW'}`);
    return () => {
      dropBreadcrumb(1);
    };
  }, [carrier]);

  useEffect(() => {
    if (!deepCompare(carrier, view)) {
      setView(carrier);
      fetchDevices();
      fetchPrefixes();
      fetchPortedNumbers();
    }
  }, [carrier]);

  useEffect(() => {
    setIsEditing(params.childId === '0');

    if (grandChild) {
      switch (grandChild) {
        case 'device':
          setSelectedCarrierDevice(
            devices.find(
              (device) => device.carrier_device_id === parseInt(grandChildId)
            ) || { carrier_device_id: 0 }
          );
          break;
        case 'prefix':
          setSelectedCarrierPrefix(
            prefixes.find(
              (prefix) => prefix.carrier_prefix_id === parseInt(grandChildId)
            ) || { carrier_prefix_id: 0 }
          );
          break;
        case 'ported-number':
          setSelectedCarrierPortedNumber(
            portedNumbers.find(
              (portedNumber) =>
                portedNumber.ported_numbers_id === parseInt(grandChildId)
            ) || { ported_numbers_id: 0 }
          );
          break;
      }

      return;
    }

    if (typeof selectedCarrierPrefix.carrier_prefix_id !== 'undefined') {
      fetchPrefixes();
      setSelectedCarrierPrefix({});
    }

    if (typeof selectedCarrierDevice.carrier_device_id !== 'undefined') {
      fetchDevices();
      setSelectedCarrierDevice({});
    }

    if (typeof selectedCarrierPortedNumber.ported_numbers_id !== 'undefined') {
      fetchPortedNumbers();
      setSelectedCarrierPortedNumber({});
    }
  }, [params]);

  const fetchDevices = async () => {
    if (!country?.country_code_id || !carrier?.carrier_id) return;

    setDevicesLoading(true);
    const fetchedDevices = await getCarrierDevices(
      country.country_code_id,
      carrier.carrier_id,
      errorHandler
    );
    setDevices(fetchedDevices);
    setDevicesLoading(false);

    if (grandChild === 'device') {
      setSelectedCarrierDevice(
        fetchedDevices.find(
          (device) => device.carrier_device_id === parseInt(grandChildId)
        ) || {}
      );
    }
  };

  const fetchPrefixes = async () => {
    if (!country?.country_code_id || !carrier?.carrier_id) return;

    setPrefixesLoading(true);
    const fetchedPrefixes = await getCarrierPrefixes(
      country.country_code_id,
      carrier.carrier_id,
      errorHandler
    );
    setPrefixes(fetchedPrefixes);
    setPrefixesLoading(false);

    if (grandChild === 'prefix') {
      setSelectedCarrierPrefix(
        fetchedPrefixes.find(
          (prefix) => prefix.carrier_prefix_id === parseInt(grandChildId)
        ) || {}
      );
    }
  };

  const fetchPortedNumbers = async () => {
    if (!country?.country_code_id || !carrier?.carrier_id) return;

    setPortedNumbersLoading(true);
    const fetchedPortedNumbers = await getCarrierPortedNumbers(
      country.country_code_id,
      carrier.carrier_id,
      errorHandler
    );
    setPortedNumbers(fetchedPortedNumbers);
    setPortedNumbersLoading(false);

    if (grandChild === 'ported-number') {
      setSelectedCarrierPortedNumber(
        fetchedPortedNumbers.find(
          (portedNumber) =>
            portedNumber.ported_numbers_id === parseInt(grandChildId)
        ) || {}
      );
    }
  };

  const toggleEdit = () => {
    if (params.childId === '0') {
      navigate(-1);
      return;
    }

    setIsEditing((prevIsEditing) => !prevIsEditing);
  };

  const handleFormChange = (name, value) => {
    setEdits((prevEdits) => ({
      ...prevEdits,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      let data = edits;

      if (carrier.carrier_id) {
        setView(
          await updateCarrier(
            country.country_code_id,
            carrier.carrier_id,
            data,
            errorHandler
          )
        );
      } else {
        setView(
          await createNewCarrier(country.country_code_id, data, errorHandler)
        );
      }

      setIsEditing(false);
    } catch (e) {
      console.log('Have err:', e);
    }
  };

  const confirmDelete = () => {
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    setShowConfirmDelete(false);
    await deleteCarrierById(
      country.country_code_id,
      carrier.carrier_id,
      errorHandler
    );
    navigate(-1);
  };

  const generateFormGroup = (label, displayValue) => {
    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong> {displayValue}
        </Typography>
      </Grid>
    );
  };

  const generateBody = () => {
    if (!carrier) {
      return <Typography>(Carrier) No Data</Typography>;
    }

    if (isEditing) {
      return (
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2} maxWidth={800}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  Carrier Name
                </InputLabel>
                <TextField
                  name="carrier_name"
                  defaultValue={carrier.carrier_name}
                  fullWidth
                  required
                  size="small"
                  onChange={(e) =>
                    handleFormChange('carrier_name', e.target.value)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} required>
                  Carrier Code
                </InputLabel>
                <TextField
                  name="carrier_code"
                  defaultValue={carrier.carrier_code}
                  fullWidth
                  required
                  size="small"
                  onChange={(e) =>
                    handleFormChange('carrier_code', e.target.value)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="accepts_alphas"
                    defaultChecked={carrier.accepts_alphas === 'Y'}
                    onChange={(e) =>
                      handleFormChange('accepts_alphas', e.target.checked)
                    }
                  />
                }
                label="Accepts Alphas"
              />
            </Grid>
            <Grid size={12}>
              <Box display="flex" gap={2}>
                <Button variant="text" color="secondary" onClick={toggleEdit}>
                  Cancel
                </Button>
                <Button variant="contained" color="secondary" type="submit">
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }

    return (
      <>
        <Grid container spacing={2}>
          <Grid size={12}>
            <form>
              <Grid container spacing={2}>
                {generateFormGroup('Carrier Name:', view.carrier_name)}
                {generateFormGroup('Carrier Code:', view.carrier_code)}
                {generateFormGroup(
                  'Accept Alphas:',
                  view.accepts_alphas === 'Y' ? 'Yes' : 'No'
                )}
              </Grid>
            </form>
          </Grid>
          <Grid size={12}>
            <Box display="flex" justifyContent="space-between" marginBottom={4}>
              <Button
                variant="contained"
                color="secondary"
                onClick={toggleEdit}
              >
                Edit
              </Button>
              <Button onClick={confirmDelete} variant="contained" color="error">
                Delete
              </Button>
            </Box>
          </Grid>
          <Grid size={12}>
            <CarrierPrefixList
              country={country}
              carrier={view}
              prefixes={prefixes}
              loading={prefixesLoading}
              navigate={navigate}
            />
          </Grid>
          <Grid size={12}>
            <CarrierPortedNumberList
              country={country}
              carrier={view}
              portedNumbers={portedNumbers}
              loading={portedNumbersLoading}
              navigate={navigate}
            />
          </Grid>
          <Grid size={12}>
            <ServiceCarrierDeviceList
              country={country}
              carrier={view}
              devices={devices}
              loading={devicesLoading}
              navigate={navigate}
            />
          </Grid>
        </Grid>
        <ConfirmModal
          show={showConfirmDelete}
          onClose={() => {
            setShowConfirmDelete(false);
          }}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this carrier?"
        />
      </>
    );
  };

  if (grandChild === 'device') {
    return (
      <CarrierDeviceSingle
        country={country}
        carrier={view}
        carrierDevice={selectedCarrierDevice}
        errorHandler={errorHandler}
        navigate={navigate}
      />
    );
  }

  if (grandChild === 'prefix') {
    return (
      <CarrierPrefixSingle
        country={country}
        carrier={view}
        carrierPrefix={selectedCarrierPrefix}
        errorHandler={errorHandler}
        navigate={navigate}
      />
    );
  }

  if (grandChild === 'ported-number') {
    return (
      <CarrierPortedNumberSingle
        country={country}
        carrier={view}
        portedNumber={selectedCarrierPortedNumber}
        errorHandler={errorHandler}
        navigate={navigate}
      />
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        sx={{ paddingBottom: 2 }}
      >
        <BackButton onClick={() => navigate(-1)} />
        <Typography variant="h4">Carrier Information</Typography>
      </Stack>

      {generateBody()}
    </Box>
  );
};

export default CarrierSingleView;
